/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import humanize from 'humanize-plus';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache } from 'react-query';
import { useDispatch } from 'react-redux';

import InfoIcon from '@mui/icons-material/Info';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import { Grid, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';

import { CIColors, ClusterStates, Endpoints, Infer, ServiceType } from 'shared';

import { SimpleNotifyModal } from '../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../../../i18n';
import { ClusterApiService } from '../../../../services/ClusterApiService';
import { ChartLoader } from '../../../../skeletons/ChartLoader';
import { NetworkChartLoader } from '../../../../skeletons/NetworkChartLoader';
import { enqueueSnackbar } from '../../../../store/notifier/actions';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { ClusterCredentialsCypexResponsePayload } from '../../../../types/cluster/ClusterCredentialsCypexResponsePayload';
import { ClusterCredentialsResponsePayload } from '../../../../types/cluster/ClusterCredentialsResponsePayload';
import { ClusterNodeListResponsePayload } from '../../../../types/cluster/ClusterNodeListResponsePayload';
import { ClusterStorageResponsePayload } from '../../../../types/cluster/ClusterStorageResponsePayload';
import { ClusterUpdateRequestBody } from '../../../../types/cluster/ClusterUpdateRequestBody';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { getBabelfishConnection } from '../../../../utils/getBabelfishConnection';
import { getCypexConnection } from '../../../../utils/getCypexConnection';
import { getPostgresConnection } from '../../../../utils/getPostgresConnection';
import { fetchNodeListOfCluster } from '../../../../utils/queryFns';
import { reactToUpdateClusterPassword } from '../../../../utils/reactions';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useProjectPermissions } from '../../../../utils/useProjectPermissions';
import { BackupsChart } from '../BackupsChart/BackupsChart';
import { HiddenPassword } from '../HiddenPassword/HiddenPassword';
import { NodesNetwork } from '../NodesChart/NodesNetwork';
import { StoragePieChart } from '../StorageCharts/StoragePieChart';
import { createDetailsTabStyles } from './DetailsTab.styles';

type DetailsTabProps = {
  serviceType: ServiceType;
  status: ClusterStates | undefined;
  credentials: ClusterCredentialsResponsePayload;
  credentialsCypex: ClusterCredentialsCypexResponsePayload;
  storage: ClusterStorageResponsePayload | undefined;
  isFetchingStorage: boolean;
  nodes: ClusterNodeListResponsePayload[];
  isFetchingNodes: boolean;
  backupsHistory: Infer<typeof Endpoints.GetClusterBackupsHistory>['Response']['200'];
  identificationData: UpdateClusterDetailsParameterIdentification;
  updateDesiredReplicas: (replicas: number) => void;
  updateData: (data: ClusterUpdateRequestBody) => Promise<any>;
};

export const DetailsTab = memo<DetailsTabProps>(props => {
  const {
    serviceType,
    status,
    credentials,
    credentialsCypex,
    storage,
    isFetchingStorage,
    identificationData,
    nodes,
    isFetchingNodes,
    backupsHistory,
    updateData,
  } = props;
  const { organizationId, projectId, clusterId } = identificationData;
  const { t } = useTranslation();
  const classes = createDetailsTabStyles();

  const dispatch = useDispatch();
  const { setApiError } = useApiValidation();

  const permissions = useProjectPermissions(organizationId, projectId);

  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    errorMessage: string;
  }>({
    isOpen: false,
    errorMessage: '',
  });

  const connectionMaster = useMemo(
    () => (credentials ? getPostgresConnection(credentials.username, credentials.host, credentials.port) : ''),
    [credentials],
  );
  const connectionLoadBalancer = useMemo(
    () =>
      credentials?.replicaPort
        ? getPostgresConnection(credentials.username, credentials.replicaHost, credentials.replicaPort)
        : '',
    [credentials?.replicaHost, credentials?.replicaPort, credentials?.username],
  );
  const connectionBabelfish = useMemo(
    () =>
      credentials?.babelfish
        ? getBabelfishConnection(credentials.username, credentials.babelfish.host, credentials.babelfish.port)
        : '',
    [credentials?.babelfish, credentials?.username],
  );

  const connectionCypex = useMemo(
    () => (credentialsCypex ? getCypexConnection(credentialsCypex.host, credentialsCypex.port) : ''),
    [credentialsCypex],
  );

  const isClusterUpdating = useMemo(
    () =>
      status === ClusterStates.Updating || status === ClusterStates.Unavailable || status === ClusterStates.Creating,
    [status],
  );

  const onOpenApplication = () => {
    window.open(connectionCypex, '_blank', 'noopener,noreferrer');
  };

  const onGeneratePassword = async () => {
    try {
      if (organizationId && projectId && clusterId) {
        await ClusterApiService.resetClusterPassword({
          organizationId,
          projectId,
          clusterId,
        });
      }
    } catch (e) {
      /* empty */
    } finally {
      await reactToUpdateClusterPassword({
        ...identificationData,
      });
    }
  };

  const onUpdateReplicas = async (replicas: number) => {
    try {
      setIsLoading(true);
      await updateData({ configuration: { replicas } });
      await queryCache.refetchQueries([fetchNodeListOfCluster.key, identificationData]);
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error);
      const responseData = error.response?.data;

      if (!responseData) {
        dispatch(
          enqueueSnackbar(error.response?.data.message || 'Something went wrong', {
            variant: 'error',
          }),
        );
      } else {
        const validationState = setApiError(error);
        if (validationState.error === 'insufficient_resources') {
          setErrorModal({
            isOpen: true,
            errorMessage: `${Namespaces.Common}:update_cluster_errors.${validationState.error}`,
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container direction="column">
      {serviceType === 'cypex' && (
        <Grid container classes={{ root: classes.detailsContainer }}>
          <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }} className={classes.detailsTitle}>
            <Typography variant="h4">
              {t(`${Namespaces.Entities}:cluster.application_connection_details.label`)}
            </Typography>
            <Tooltip title={t(`${Namespaces.Cluster}:details.application.open_in_new_tab`)}>
              <IconButton
                onClick={onOpenApplication}
                disabled={!connectionCypex}
                sx={{ padding: 0, marginLeft: '5px' }}
              >
                <OpenInNewIcon sx={{ height: '18px', width: '18px' }} />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid container item>
            <Grid container item xs={12} classes={{ root: classes.detailsRow }}>
              <Typography variant="body1" component="div" classes={{ root: classes.detailsKey }}>
                {t(`${Namespaces.Entities}:cluster.connection_details.username.label`)}:
              </Typography>
              {credentialsCypex.username ? (
                <Typography variant="body1" component="div" classes={{ root: classes.detailsValue }}>
                  <span>{credentialsCypex.username}</span>
                </Typography>
              ) : (
                <Skeleton width={250} />
              )}
            </Grid>
            <Grid container item xs={12} classes={{ root: classes.detailsRow }}>
              <Typography variant="body1" component="div" classes={{ root: classes.detailsKey }}>
                {t(`${Namespaces.Entities}:cluster.connection_details.password.label`)}:
              </Typography>
              {credentialsCypex.password ? (
                <Typography variant="body1" component="div" classes={{ root: classes.detailsValue }}>
                  <HiddenPassword
                    password={credentialsCypex.password}
                    onGeneratePassword={() => {
                      return Promise.resolve();
                    }}
                    disabled={isClusterUpdating}
                    showGenerateButton={false}
                  />
                </Typography>
              ) : (
                <Skeleton width={250} />
              )}
            </Grid>
          </Grid>
        </Grid>
      )}

      <Grid container classes={{ root: classes.detailsContainer }}>
        <Grid item xs={12}>
          <Typography variant="h4" classes={{ root: cx(classes.detailsTitle) }}>
            {t(`${Namespaces.Entities}:cluster.connection_details.label`)}
          </Typography>
        </Grid>
        <Grid container item>
          <Grid container item xs={12} classes={{ root: cx(classes.detailsRow, classes.addressRow) }}>
            <Typography variant="body1" component="div" classes={{ root: classes.detailsKey }}>
              {t(`${Namespaces.Cluster}:master`)}:
            </Typography>
            {credentials.username ? (
              <Typography variant="body1" component="div" classes={{ root: classes.address }}>
                <code>{connectionMaster}</code>
              </Typography>
            ) : (
              <Skeleton width={250} />
            )}
          </Grid>
          {connectionLoadBalancer && (
            <Grid container item xs={12} classes={{ root: cx(classes.detailsRow, classes.addressRow) }}>
              <Typography variant="body1" component="div" classes={{ root: classes.detailsKey }}>
                {t(`${Namespaces.Entities}:cluster.connection_details.read_only.label`)}:
              </Typography>
              <Typography variant="body1" component="div" classes={{ root: classes.address }}>
                <code>{connectionLoadBalancer}</code>
              </Typography>
            </Grid>
          )}
          {connectionBabelfish && (
            <Grid container item xs={12} classes={{ root: cx(classes.detailsRow, classes.addressRow) }}>
              <Typography variant="body1" component="div" classes={{ root: classes.detailsKey }}>
                {t(`${Namespaces.Cluster}:babelfish`)}:
              </Typography>
              <Typography variant="body1" component="div" classes={{ root: classes.address }}>
                <code>{connectionBabelfish}</code>
              </Typography>
            </Grid>
          )}
          <Grid container item xs={12} classes={{ root: classes.detailsRow }}>
            <Typography variant="body1" component="div" classes={{ root: classes.detailsKey }}>
              {t(`${Namespaces.Entities}:cluster.connection_details.username.label`)}:
            </Typography>
            {credentials.username ? (
              <Typography variant="body1" component="div" classes={{ root: classes.detailsValue }}>
                <span>{credentials.username}</span>
              </Typography>
            ) : (
              <Skeleton width={250} />
            )}
          </Grid>
          <Grid container item xs={12} classes={{ root: classes.detailsRow }}>
            <Typography variant="body1" component="div" classes={{ root: classes.detailsKey }}>
              {t(`${Namespaces.Entities}:cluster.connection_details.password.label`)}:
            </Typography>
            {credentials.password ? (
              <Typography variant="body1" component="div" classes={{ root: classes.detailsValue }}>
                <HiddenPassword
                  password={credentials.password}
                  onGeneratePassword={onGeneratePassword}
                  disabled={isClusterUpdating}
                  showGenerateButton={permissions.edit_cluster}
                />
              </Typography>
            ) : (
              <Skeleton width={250} />
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid container sx={{ gap: '25px' }}>
        <Grid item>
          <Typography variant="h4" classes={{ root: cx(classes.descriptionTitle) }}>
            {t(`${Namespaces.Cluster}:details.storage.title`)}
          </Typography>
          {isFetchingStorage ? (
            <ChartLoader />
          ) : (
            storage &&
            (storage.filesystem === 'StorageError' ? (
              <Typography variant="body1" component="div" classes={{ root: classes.storageErrorTitle }}>
                <div className={classes.storageError}>
                  {t(`${Namespaces.Cluster}:details.storage.error.title`)}
                  <Tooltip title={storage.mounted} placement="right" classes={{ tooltip: classes.tooltip }}>
                    <span>
                      <IconButton size="small" classes={{ root: classes.infoIcon }}>
                        <InfoIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              </Typography>
            ) : (
              <StoragePieChart
                data={[
                  {
                    id: 'used',
                    name: t(`${Namespaces.Cluster}:details.storage.used`),
                    value: parseInt(storage.usedPercent),
                    memory: `(${humanize.fileSize(storage.usedRaw * Math.pow(1024, 2), 0)})`,
                    icon: 'none',
                    textStyle: {
                      color: CIColors.Primary,
                    },
                    itemStyle: {
                      color: CIColors.Primary,
                    },
                  },
                  {
                    id: 'available',
                    name: t(`${Namespaces.Cluster}:details.storage.available`),
                    value: 100 - parseInt(storage.usedPercent),
                    memory: `(${humanize.fileSize(storage.available * Math.pow(1024, 2), 0)})`,
                    icon: 'none',
                    textStyle: {
                      color: CIColors.Secondary,
                    },
                    itemStyle: {
                      color: CIColors.Secondary,
                    },
                  },
                ]}
              />
            ))
          )}
        </Grid>
        {serviceType !== 'cypex' && (
          <Grid item>
            <Typography variant="h4" classes={{ root: cx(classes.descriptionTitle) }}>
              {t(`${Namespaces.Cluster}:details.replicas.title`)}
            </Typography>
            {isFetchingNodes || !nodes.length ? (
              <NetworkChartLoader />
            ) : (
              <NodesNetwork nodes={nodes} updateReplicas={onUpdateReplicas} isLoading={isLoading} />
            )}
          </Grid>
        )}
      </Grid>

      <Grid item>
        <BackupsChart backupsHistory={backupsHistory} />
      </Grid>

      <SimpleNotifyModal
        open={errorModal.isOpen}
        message={t([errorModal.errorMessage, `${Namespaces.Common}:errors.unexpected_error`])}
        onOk={() =>
          setErrorModal({
            isOpen: false,
            errorMessage: '',
          })
        }
      />
    </Grid>
  );
});
